import { useEffect, useRef } from 'react';

// Unsaved work, app-wide: the pages that track unsaved changes register them here, so code outside the page can ask
// "is something unsaved?" - the access-hold banner says so - and the browser warns before the tab is closed or
// reloaded with something unsaved (nothing did before: a reload lost it silently).

const entries = new Map();
let installed = false;

/** The browser's leave-site warning while anything is unsaved. Exported for the tests. */
export const beforeUnloadHandler = (event) => {
    if (entries.size === 0) return undefined;
    event.preventDefault();
    // Chrome needs returnValue set; the text itself is the browser's own.
    event.returnValue = '';
    return '';
};

const sync = () => {
    if (typeof window === 'undefined') return;
    if (entries.size > 0 && !installed) {
        window.addEventListener('beforeunload', beforeUnloadHandler);
        installed = true;
    } else if (entries.size === 0 && installed) {
        window.removeEventListener('beforeunload', beforeUnloadHandler);
        installed = false;
    }
};

let nextId = 0;
export const markUnsaved = (label = 'Unsaved changes') => {
    nextId += 1;
    const id = `uw-${nextId}`;
    entries.set(id, label);
    sync();
    return id;
};

export const clearUnsaved = (id) => {
    if (entries.delete(id)) sync();
};

export const hasUnsavedWork = () => entries.size > 0;

/** What is unsaved, by the labels the pages gave it. */
export const unsavedWorkLabels = () => [...entries.values()];

/** While `isDirty`, this page has unsaved work. Clears itself when it is saved, discarded or the page unmounts. */
export const useUnsavedWork = (isDirty, label) => {
    const idRef = useRef(null);
    useEffect(() => {
        if (isDirty && !idRef.current) idRef.current = markUnsaved(label);
        if (!isDirty && idRef.current) {
            clearUnsaved(idRef.current);
            idRef.current = null;
        }
    }, [isDirty, label]);
    useEffect(() => () => {
        if (idRef.current) clearUnsaved(idRef.current);
        idRef.current = null;
    }, []);
};
